/**
 * /accounting/gl/audit-log — Append-only journal-entry audit trail.
 *
 * One row per significant event: post, reverse, void, period close/reopen.
 * Read-only surface derived entirely from the JE stream + periodLocks —
 * no separate audit table to drift.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Filter } from "lucide-react";
import { CockpitPage, CockpitSection, CockpitSearch } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import { exportCsv } from "@/lib/csvExport";

type AuditEventType = "post" | "reverse" | "void" | "period_close" | "period_reopen";

interface AuditEvent {
  at: string;             // ISO timestamp
  type: AuditEventType;
  actor: string;
  refLabel: string;       // JE number, period id, etc.
  description: string;
  amount?: number;        // total debits for the JE, if applicable
}

const TYPE_LABEL: Record<AuditEventType, string> = {
  post: "Posted",
  reverse: "Reversed",
  void: "Voided",
  period_close: "Period closed",
  period_reopen: "Period reopened",
};

const TYPE_TONE: Record<AuditEventType, string> = {
  post: "",
  reverse: "border-amber-500/40 text-amber-600 dark:text-amber-500",
  void: "border-destructive/40 text-destructive",
  period_close: "border-primary/40 text-primary",
  period_reopen: "border-amber-500/40 text-amber-600 dark:text-amber-500",
};

const fmtMoney = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AuditLogPage() {
  useSeedGeneralLedger();
  const { journalEntries, periodLocks } = useGeneralLedger();
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState<"__all" | AuditEventType>("__all");

  const events = useMemo<AuditEvent[]>(() => {
    const out: AuditEvent[] = [];
    for (const je of journalEntries) {
      const total = je.lines
        .filter(l => l.side === "debit")
        .reduce((s, l) => s + l.amount, 0);
      if (je.postedAt) {
        out.push({
          at: je.postedAt,
          type: "post",
          actor: je.postedBy ?? je.createdBy,
          refLabel: je.number,
          description: je.description,
          amount: total,
        });
      }
      if (je.status === "void" && je.voidedAt) {
        // Distinguish reversal (had a paired reverse JE) from explicit void.
        // Both surface as type="void" but the JE that did the reversing posts
        // its own "post" row above, so this row is the original being voided.
        out.push({
          at: je.voidedAt,
          type: je.voidReason?.toLowerCase().includes("reversal") ? "reverse" : "void",
          actor: je.voidedBy ?? je.createdBy,
          refLabel: je.number,
          description: je.voidReason ?? "Voided",
          amount: total,
        });
      }
    }
    for (const lock of periodLocks) {
      if (lock.closedAt) {
        out.push({
          at: lock.closedAt,
          type: lock.status === "closed" ? "period_close" : "period_reopen",
          actor: lock.closedBy ?? "system",
          refLabel: lock.period,
          description: lock.reason ?? (lock.status === "closed" ? "Period closed" : "Period reopened"),
        });
      }
    }
    return out.sort((a, b) => b.at.localeCompare(a.at));
  }, [journalEntries, periodLocks]);

  const filtered = useMemo(() => {
    let rows = events;
    if (typeFilter !== "__all") rows = rows.filter(e => e.type === typeFilter);
    if (q.trim()) {
      const s = q.toLowerCase();
      rows = rows.filter(e =>
        e.refLabel.toLowerCase().includes(s) ||
        e.description.toLowerCase().includes(s) ||
        e.actor.toLowerCase().includes(s));
    }
    return rows;
  }, [events, q, typeFilter]);

  const downloadCsv = () => {
    exportCsv(
      `audit-log-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Timestamp", "Event", "Reference", "Actor", "Description", "Amount"],
      filtered.map(e => [
        e.at, TYPE_LABEL[e.type], e.refLabel, e.actor, e.description,
        e.amount != null ? e.amount.toFixed(2) : "",
      ]),
    );
  };

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/accounting/gl" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Back to General Ledger
          </Link>
          <h1 data-tour-id="audit-log-list" className="text-2xl font-semibold tracking-tight">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every post, reversal, void, and period-close event — append-only and derived directly from the ledger.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="w-64"><CockpitSearch value={q} onChange={setQ} placeholder="JE#, actor, description…" /></div>
          <div className="w-40">
            <Select value={typeFilter} onValueChange={(v: typeof typeFilter) => setTypeFilter(v)}>
              <SelectTrigger className="h-9"><Filter className="h-3.5 w-3.5 mr-1.5" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All events</SelectItem>
                <SelectItem value="post">Posted</SelectItem>
                <SelectItem value="reverse">Reversed</SelectItem>
                <SelectItem value="void">Voided</SelectItem>
                <SelectItem value="period_close">Period closed</SelectItem>
                <SelectItem value="period_reopen">Period reopened</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={downloadCsv} disabled={filtered.length === 0}>
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 w-44">Timestamp</th>
                <th className="text-left px-3 py-2 w-32">Event</th>
                <th className="text-left px-3 py-2 w-32">Reference</th>
                <th className="text-left px-3 py-2 w-32">Actor</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-right px-3 py-2 w-32">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">No events match.</td></tr>
              )}
              {filtered.map((e, i) => (
                <tr key={i} className="border-t border-border hover:bg-accent/30">
                  <td className="px-3 py-1.5 text-xs text-muted-foreground tabular-nums">
                    {e.at.replace("T", " ").slice(0, 19)}
                  </td>
                  <td className="px-3 py-1.5">
                    <Badge variant="outline" className={`text-[10px] ${TYPE_TONE[e.type]}`}>
                      {TYPE_LABEL[e.type]}
                    </Badge>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">{e.refLabel}</td>
                  <td className="px-3 py-1.5 text-xs">{e.actor}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground truncate max-w-md">{e.description}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-xs">
                    {e.amount != null ? fmtMoney(e.amount) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">
          {filtered.length} event{filtered.length === 1 ? "" : "s"} · this view is read-only and never persisted separately — it's recomputed from the journal on every render.
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}
